import React from 'react';

import useStyles from '../../styles/report';
import { MaxMinAvg, SensorStatsSummary } from '../../utils/statistics';
import { VarName, varNameDetails } from '../../utils/varNames';
import { getDataBandParams, varNameBandParams } from '../../utils/dataBandParams';

interface AnalysisData {
  varName: VarName;
  statsPerSensor: SensorStatsSummary[] | undefined;
  showMinMaxAvg: boolean;
}

function AnalysisSection({ varName, statsPerSensor, showMinMaxAvg }: AnalysisData): JSX.Element {
  const classes = useStyles();

  if (!statsPerSensor?.length) {
    return <h3>No data</h3>;
  }

  const bandDetails = varNameBandParams[varName];

  let max = -Infinity;
  let min = Infinity;
  const means: number[] = [];

  if (bandDetails) {
    const bandAnalysis = bandDetails.map((band) => {
      let totalTime = 0;
      let totalValue = 0;

      statsPerSensor.forEach(({ stats }) => {
        totalTime += stats.totalTime;
        totalValue += stats.bandTimes.get(band.label) ?? 0;
        max = Math.max(max, stats.maxMinAvg.max);
        min = Math.min(min, stats.maxMinAvg.min);
        means.push(stats.maxMinAvg.mean);
      });

      return {
        band,
        totalValue,
        totalTime,
        pct: (100 * totalValue) / totalTime,
      };
    });
    const sum = means?.reduce((a, b) => a + b, 0) ?? 0;
    const mean = sum / Math.max(means.length, 1);
    const maxMinAvg: MaxMinAvg = { max, min, mean };

    return (
      <>
        {showMinMaxAvg && (
          <>
            Table (below): Minimum, average (mean) and maximum values per sensor.
            <table className={classes.summaryTable}>
              <tbody>
                <tr key={`${varName}-vals-header`}>
                  <th> </th>
                  <th>Minimum</th>
                  <th>Average</th>
                  <th>Maximum</th>
                </tr>
                <tr key={`${varName}-AllSensors`}>
                  <td>
                    <b>All sensors</b>
                  </td>
                  <td
                    className={classes.summaryTableBandCell}
                    style={{ background: getDataBandParams(varName, maxMinAvg.min)?.color }}
                  >
                    {`${maxMinAvg.min} ${varNameDetails[varName].metric ?? ''}`}
                  </td>
                  <td
                    className={classes.summaryTableBandCell}
                    style={{ background: getDataBandParams(varName, maxMinAvg.mean)?.color }}
                  >
                    {`${maxMinAvg.mean.toFixed(0)} ${varNameDetails[varName].metric ?? ''}`}
                  </td>
                  <td
                    className={classes.summaryTableBandCell}
                    style={{ background: getDataBandParams(varName, maxMinAvg.max)?.color }}
                  >
                    {`${maxMinAvg.max} ${varNameDetails[varName].metric ?? ''}`}
                  </td>
                </tr>
                {statsPerSensor
                  .sort((a, b) => a.sensorName.localeCompare(b.sensorName))
                  .map((s) => (
                    <tr key={`${varName}-${s.sensorId}`}>
                      <td>{s.sensorName}</td>
                      <td
                        className={classes.summaryTableBandCell}
                        style={{
                          background: getDataBandParams(varName, s.stats.maxMinAvg.min)?.color,
                        }}
                      >
                        {`${s.stats.maxMinAvg.min} ${varNameDetails[varName].metric ?? ''}`}
                      </td>
                      <td
                        className={classes.summaryTableBandCell}
                        style={{
                          background: getDataBandParams(varName, s.stats.maxMinAvg.mean)?.color,
                        }}
                      >
                        {`${s.stats.maxMinAvg.mean.toFixed(0)} ${
                          varNameDetails[varName].metric ?? ''
                        }`}
                      </td>
                      <td
                        className={classes.summaryTableBandCell}
                        style={{
                          background: getDataBandParams(varName, s.stats.maxMinAvg.max)?.color,
                        }}
                      >
                        {`${s.stats.maxMinAvg.max} ${varNameDetails[varName].metric ?? ''}`}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <br />
          </>
        )}
        Table (below): Percentage of time measured in each band range. (For example if the sensor
        spends 1 of every 4 hours within a particular range and 3 of 4 in another those bands will
        show 25% and 75%.)
        <table className={classes.summaryTable} style={{ marginTop: '10px' }}>
          <tbody>
            <tr key={`${varName}-header`}>
              <th> </th>
              {bandDetails.map((b) => (
                <th key={`${varName}-band-${b.label}`}>{b.label}</th>
              ))}
            </tr>
            <tr key={`${varName}-AllSensors`}>
              <td>
                <b>All sensors</b>
              </td>
              {bandAnalysis.map((b) => (
                <td
                  key={`${varName}-bandAnalysis-${b.band.label}`}
                  className={classes.summaryTableBandCell}
                  style={{ background: b.band.color }}
                >{`${b.pct.toFixed(1)}%`}</td>
              ))}
            </tr>
            {statsPerSensor
              .sort((a, b) => a.sensorName.localeCompare(b.sensorName))
              .map((s) => (
                <tr key={`${varName}-${s.sensorId}`}>
                  <td>{s.sensorName}</td>
                  {bandDetails.map((b) => {
                    const pct = (100 * (s.stats.bandTimes.get(b.label) ?? 0)) / s.stats.totalTime;
                    return (
                      <td
                        key={`${varName}-${s.sensorId}-band-${b.label}`}
                        className={classes.summaryTableBandCell}
                        style={pct > 0 ? { background: b.color } : { background: 'lightgrey' }}
                      >
                        {pct.toFixed(1)}%
                      </td>
                    );
                  })}
                </tr>
              ))}
          </tbody>
        </table>
      </>
    );
  }

  // Cumulative data calculate the total
  if (varName === VarName.EnergyInkWh) {
    const totals: number[] = [];
    statsPerSensor.forEach(({ stats }) => {
      totals.push(stats.totalAdded ?? 0);
    });

    const sum = totals.reduce((a, b) => a + b);
    return (
      <>
        <h3>Analysis</h3>
        <h4>Summary</h4>
        Table (below): Total increase in recorded energy per sensor.
        <table className={classes.summaryTable}>
          <tbody>
            <tr key={`${varName}-vals-header`}>
              <th> </th>
              <th>Total</th>
            </tr>
            <tr key={`${varName}-AllSensors`}>
              <td>
                <b>All sensors</b>
              </td>
              <td
                className={classes.summaryTableBandCell}
                style={{ background: getDataBandParams(varName, sum)?.color }}
              >
                {`${sum.toFixed(1)} ${varNameDetails[varName].metric ?? ''}`}
              </td>
            </tr>
            {statsPerSensor
              .sort((a, b) => a.sensorName.localeCompare(b.sensorName))
              .map((s) => (
                <tr key={`${varName}-${s.sensorId}`}>
                  <td>{s.sensorName}</td>
                  <td className={classes.summaryTableBandCell} style={{ background: 'grey' }}>
                    {`${s.stats.totalAdded?.toFixed(1)} ${varNameDetails[varName].metric ?? ''}`}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </>
    );
  }

  // No band details, do a basic summary
  statsPerSensor.forEach(({ stats }) => {
    max = Math.max(max, stats.maxMinAvg.max);
    min = Math.min(min, stats.maxMinAvg.min);
    means.push(stats.maxMinAvg.mean);
  });

  const sum = means.reduce((a, b) => a + b);
  const mean = sum / means.length;
  const maxMinAvg: MaxMinAvg = { max, min, mean };
  return (
    <>
      <h3>Analysis</h3>
      <h4>Summary</h4>
      Table (below): Minimum, average (mean) and maximum values per sensor.
      <table className={classes.summaryTable}>
        <tbody>
          <tr key={`${varName}-vals-header`}>
            <th> </th>
            <th>Minimum</th>
            <th>Average</th>
            <th>Maximum</th>
          </tr>
          <tr key={`${varName}-AllSensors`}>
            <td>
              <b>All sensors</b>
            </td>
            <td
              className={classes.summaryTableBandCell}
              style={{ background: getDataBandParams(varName, maxMinAvg.min)?.color }}
            >
              {`${maxMinAvg.min} ${varNameDetails[varName].metric ?? ''}`}
            </td>
            <td
              className={classes.summaryTableBandCell}
              style={{ background: getDataBandParams(varName, maxMinAvg.mean)?.color }}
            >
              {`${maxMinAvg.mean.toFixed(0)} ${varNameDetails[varName].metric ?? ''}`}
            </td>
            <td
              className={classes.summaryTableBandCell}
              style={{ background: getDataBandParams(varName, maxMinAvg.max)?.color }}
            >
              {`${maxMinAvg.max} ${varNameDetails[varName].metric ?? ''}`}
            </td>
          </tr>
          {statsPerSensor
            .sort((a, b) => a.sensorName.localeCompare(b.sensorName))
            .map((s) => (
              <tr key={`${varName}-${s.sensorId}`}>
                <td>{s.sensorName}</td>
                <td className={classes.summaryTableBandCell} style={{ background: 'grey' }}>
                  {`${s.stats.maxMinAvg.min} ${varNameDetails[varName].metric ?? ''}`}
                </td>
                <td className={classes.summaryTableBandCell} style={{ background: 'grey' }}>
                  {`${s.stats.maxMinAvg.mean.toFixed(0)} ${varNameDetails[varName].metric ?? ''}`}
                </td>
                <td className={classes.summaryTableBandCell} style={{ background: 'grey' }}>
                  {`${s.stats.maxMinAvg.max} ${varNameDetails[varName].metric ?? ''}`}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      <p>No band details</p>
    </>
  );
}

export default AnalysisSection;
